/**
 * The imported bank debits that can be ONE outgoing transfer — a sent payout or a return
 * leaving the account — for a manager to pick from. Nothing is chosen for them: the line
 * whose identifiers match is only marked (M-13: structured links and two-person picks,
 * never a typed id).
 */
import type { BankDebitCandidate } from '../../services/payouts.service';
import { formatDateTime, formatMoney } from '../../lib/datetime';
import { colors } from '../../styles/theme';

export default function DebitPicker({
  tied = false,
  candidates,
  picked,
  onPick,
  name = 'bank-debit',
  legend = 'Bank debit that is this transfer (optional)',
  emptyText = 'No imported debit fits this amount and date yet. Import the statement and come back, or confirm without one.',
  allowNone = true,
}: Readonly<{
  /** The statement import already tied the transfer to a debit: nothing to pick. */
  tied?: boolean;
  candidates: BankDebitCandidate[] | null;
  picked: string;
  onPick: (id: string) => void;
  /** Radio-group name; unique per picker when several are on one screen. */
  name?: string;
  legend?: string;
  emptyText?: string;
  /** Whether leaving the choice empty is a valid answer (payouts: yes; returns: no). */
  allowNone?: boolean;
}>): JSX.Element {
  if (tied) {
    return (
      <p className="text-xs sm:col-span-3" style={{ color: colors.muted }}>
        The statement import already tied this transfer to a bank debit.
      </p>
    );
  }
  return (
    <fieldset className="sm:col-span-3">
      <legend className="text-xs font-semibold mb-1" style={{ color: colors.inkSoft }}>
        {legend}
      </legend>
      {candidates === null && <p className="text-xs" style={{ color: colors.muted }}>Loading…</p>}
      {candidates !== null && candidates.length === 0 && (
        <p className="text-xs" style={{ color: colors.muted }}>{emptyText}</p>
      )}
      {candidates !== null && candidates.length > 0 && (
        <>
          {allowNone && (
            <label className="flex items-center gap-2 text-xs py-1">
              <input type="radio" name={name} checked={picked === ''} onChange={() => onPick('')} />
              <span>None of these</span>
            </label>
          )}
          {candidates.map((c) => (
            <label key={c.bank_transaction_id} className="flex items-center gap-2 text-xs py-1">
              <input type="radio" name={name} checked={picked === c.bank_transaction_id}
                     onChange={() => onPick(c.bank_transaction_id)} />
              <span>
                {formatDateTime(c.posted_at)} · {formatMoney(c.amount, c.currency_code)}
                {c.account_label && <> · {c.account_label}</>}
                {c.rail_trace && <> · <span className="font-mono">{c.rail_trace}</span></>}
                {c.trace_matches && <> · <strong>same identifier as recorded</strong></>}
                {c.beneficiary_matches && <> · <strong>same beneficiary</strong></>}
              </span>
            </label>
          ))}
        </>
      )}
    </fieldset>
  );
}
